import {
  ColDef,
  GridOptions,
  ICellRendererParams,
  themeAlpine,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import orderBy from "lodash.orderby";
import { Ban, Check } from "lucide-react";
import { DateTime } from "luxon";
import React, { useEffect, useMemo, useRef } from "react";

import { IgLink, IgTableWrapper } from "@intelligems/ig-theme";
import { PluginPerformanceType } from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { theme } from "@src/theme";

import { dataFormatter, titleFormatter } from "./utils";

const myTheme = themeAlpine.withParams({
  fontFamily: theme.typography.fontFamily,
});
interface PerformanceTableProps {
  data: PluginPerformanceType[];
}

export const PerformanceTable = ({ data }: PerformanceTableProps) => {
  const gridRef = useRef<AgGridReact>(null);

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: 1,
      minWidth: 100,
      headerClass: "performance-header",
      resizable: false,
      cellStyle: { fontSize: "10px" },
      menuTabs: [],
      suppressMovable: true,
      suppressHeaderMenuButton: true,
      sortable: false,
    };
  }, []);

  const columnDefs: ColDef[] = useMemo(() => {
    const cols: ColDef[] = [
      {
        headerName: "Date",
        field: "completed",
        cellRenderer: (item) => {
          return DateTime.fromSeconds(item.value).toFormat("yyyy-MM-dd hh:mm");
        },
      },
      {
        headerName: "Test ID",
        field: "id",
        // eslint-disable-next-line react/no-unstable-nested-components
        cellRenderer: (item) => {
          return (
            <IgLink
              onClick={() => {
                window.open(item.data.summary, "_blank");
              }}
            >
              {item.value}
            </IgLink>
          );
        },
      },
      {
        headerName: "Theme ID",
        field: "themeId",
        cellRenderer: (item: ICellRendererParams) => {
          return item.value || "Live";
        },
      },
      {
        headerName: "IG Loaded",
        field: "blockIntelligems",
        // eslint-disable-next-line react/no-unstable-nested-components
        cellRenderer: (item) => {
          if (item.value === true) {
            return <Ban color={theme.igIconColors.error} size={16} />;
          }
          return <Check size={16} color={theme.igIconColors.success} />;
        },
      },
    ];

    let dataCols: ColDef[] = [];
    if (data && data.length && data[0].results) {
      for (const _key of Object.keys(data[0].results)) {
        const key = _key.replace("-blocked", "");
        dataCols.push({
          headerName: titleFormatter(key, false),
          field: `results.${key}`,
          cellRenderer: (item) => {
            if (item?.data?.results) {
              if (item.data.results[key] !== undefined) {
                return dataFormatter(key, item.data.results[key]);
              }
              if (item.data.results[`${key}-blocked`] !== undefined) {
                return dataFormatter(key, item.data.results[`${key}-blocked`]);
              }
            }
            return null;
          },
        });
      }
    }
    dataCols = orderBy(dataCols, (_) => _.headerName);
    return cols.concat(dataCols);
  }, [data]);

  const gridOptions = useMemo<GridOptions>(() => {
    return {
      resizable: true,
      initialWidth: 200,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      getRowId: (params) => {
        return params.data.id;
      },
      rowData: data,
    };
  }, []);

  useEffect(() => {
    gridRef.current?.api?.redrawRows();
  }, [data]);

  if (!data) {
    return null;
  }

  return (
    <Grid
      item
      rowSpacing={2}
      sx={{
        width: "100%",
        "& .ag-header-cell": {
          fontSize: "12px",
        },
      }}
    >
      <IgTableWrapper>
        <AgGridReact
          ref={gridRef}
          theme={myTheme}
          suppressContextMenu={true}
          suppressCsvExport={true}
          defaultColDef={defaultColDef}
          rowData={orderBy(data, (_) => _.completed, "desc")}
          columnDefs={columnDefs}
          suppressAggFuncInHeader={true}
          gridOptions={gridOptions}
          suppressDragLeaveHidesColumns={true}
        />
      </IgTableWrapper>
    </Grid>
  );
};
